import * as React from 'react';
import { Check, ChevronsUpDown } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Spinner } from '@/components/ui/spinner';
import { cn } from '@/lib/utils';

export interface ComboboxOption {
  value: string;
  /** Texto exibido no trigger quando selecionado */
  label: string;
  /** Palavras extras para busca (ex: UF, sinônimos). cmdk usa para filtrar */
  keywords?: string[];
  /** O texto buscado no lugar do `label` (ex.: o rótulo traz contagem ou frase que não se busca). */
  searchText?: string;
}

interface ComboboxBaseProps {
  /** Vai para o botão: o `<Label htmlFor>` aponta para ele. */
  id?: string;
  value: string;
  onValueChange: (value: string) => void;
  options: ComboboxOption[];
  placeholder?: string;
  searchPlaceholder?: string;
  emptyMessage?: string;
  hasError?: boolean;
  className?: string;
  /** Renderizar conteúdo customizado dentro do item */
  renderOption?: (option: ComboboxOption) => React.ReactNode;
  /**
   * Ação fixa no rodapé da lista (ex.: "Convidar por e-mail…"). Fica FORA da área
   * filtrada: sempre visível, com ou sem resultado, e fecha a lista ao ser escolhida.
   */
  footerAction?: { label: string; icon?: React.ReactNode; onSelect: () => void };
}

/** Props extras para modo assíncrono (busca server-side) */
interface ComboboxAsyncProps {
  /** Controlar o valor do search externamente */
  searchValue?: string;
  onSearchChange?: (search: string) => void;
  /** Desabilitar filtro do cmdk (para busca server-side) */
  shouldFilter?: false;
  loading?: boolean;
  /** Mínimo de caracteres para buscar */
  minSearchLength?: number;
  minSearchMessage?: string;
}

export type ComboboxProps = ComboboxBaseProps & ComboboxAsyncProps;

/**
 * Valor interno para a opção cujo valor real é string vazia ("Todas as roles",
 * "Pelo nome"). O cmdk trata `value=""` como ausência de valor: `score()` devolve
 * 0 antes de chamar o nosso filtro (a opção some assim que se digita), o
 * `aria-selected` fica preso em false e, por consequência, a navegação por setas
 * nunca acha o item atual — o teclado não seleciona NADA na lista, nem essa opção
 * nem as outras. Trocar por um sentinel na fronteira com o cmdk resolve sem que
 * quem usa o componente precise saber disso.
 */
const VALOR_VAZIO = '__combobox_vazio__';

/** A busca ignora acento e caixa dos dois lados: "maceio" acha "Maceió". */
const semAcento = (texto: string) => texto.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

const paraCmdk = (valor: string) => (valor === '' ? VALOR_VAZIO : valor);
const doCmdk = (valor: string) => (valor === VALOR_VAZIO ? '' : valor);

/**
 * Lista sem portal: `absolute` por cima do conteúdo — mas DENTRO de um modal
 * vai para o fluxo, porque o modal tem teto de altura com rolagem e cortaria
 * a lista na borda. No fluxo, ela empurra o rodapé e o modal cresce (ou rola).
 */
const LIST_POSITION = 'absolute z-50 [[role=dialog]_&]:relative';

export function Combobox({
  id,
  value,
  onValueChange,
  options,
  placeholder = 'Selecione...',
  searchPlaceholder = 'Buscar...',
  emptyMessage = 'Nenhum resultado encontrado.',
  hasError,
  className,
  renderOption,
  footerAction,
  searchValue,
  onSearchChange,
  shouldFilter,
  loading,
  minSearchLength,
  minSearchMessage = 'Digite para buscar...',
}: ComboboxProps) {
  const [open, setOpen] = React.useState(false);
  const wrapperRef = React.useRef<HTMLDivElement>(null);

  const selected = options.find((o) => o.value === value);

  const showMinSearchHint =
    minSearchLength != null &&
    (searchValue ?? '').length < minSearchLength &&
    options.length === 0;

  // Auto-focus no input de busca ao abrir
  React.useEffect(() => {
    if (!open) return;
    requestAnimationFrame(() => {
      wrapperRef.current?.querySelector<HTMLInputElement>('[cmdk-input]')?.focus();
    });
  }, [open]);

  // Fechar ao clicar fora
  React.useEffect(() => {
    if (!open) return;
    function handleClickOutside(e: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  return (
    <div ref={wrapperRef} className="relative" data-dropdown-open={open ? '' : undefined}>
      <Button
        id={id}
        type="button"
        variant="outline"
        role="combobox"
        aria-expanded={open}
        onClick={() => setOpen(!open)}
        className={cn(
          'h-auto min-h-9 w-full justify-between whitespace-normal py-2 text-left font-normal focus:outline-none focus:ring-1 focus:ring-ring',
          !value && 'text-muted-foreground',
          hasError && 'border-destructive',
          className,
        )}
      >
        <span className="min-w-0 break-words">
          {selected ? selected.label : placeholder}
        </span>
        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
      </Button>

      {open && (
        <div className={cn(LIST_POSITION, 'mt-1 w-full rounded-md border bg-popover text-popover-foreground shadow-md animate-in fade-in-0 zoom-in-95')}>
          <Command
            shouldFilter={shouldFilter ?? true}
            filter={
              shouldFilter === false
                ? undefined
                : (val, search) => {
                    const opt = options.find((o) => paraCmdk(o.value) === val);
                    if (!opt) return 0;
                    const hay = semAcento([opt.searchText ?? opt.label, ...(opt.keywords ?? [])].join(' '));
                    return hay.includes(semAcento(search)) ? 1 : 0;
                  }
            }
          >
            <CommandInput
              placeholder={searchPlaceholder}
              value={searchValue}
              onValueChange={onSearchChange}
              onKeyDown={(e) => {
                if (e.key === 'Escape') setOpen(false);
              }}
            />
            <CommandList>
              {loading ? (
                <div className="flex justify-center py-4">
                  <Spinner size={16} label="Buscando..." />
                </div>
              ) : showMinSearchHint ? (
                <p className="py-4 text-center text-sm text-muted-foreground">
                  {minSearchMessage}
                </p>
              ) : (
                <>
                  <CommandEmpty>{emptyMessage}</CommandEmpty>
                  {options.map((opt) => (
                    <CommandItem
                      key={opt.value}
                      value={paraCmdk(opt.value)}
                      keywords={opt.keywords}
                      onSelect={(val) => {
                        const escolhido = doCmdk(val);
                        onValueChange(escolhido === value ? '' : escolhido);
                        setOpen(false);
                      }}
                    >
                      <Check
                        className={cn(
                          'mr-2 h-4 w-4 shrink-0',
                          value === opt.value ? 'opacity-100' : 'opacity-0',
                        )}
                      />
                      {renderOption ? renderOption(opt) : opt.label}
                    </CommandItem>
                  ))}
                </>
              )}
            </CommandList>
            {footerAction && (
              <div className="border-t p-1">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="w-full justify-start font-normal text-primary"
                  onClick={() => {
                    setOpen(false);
                    footerAction.onSelect();
                  }}
                >
                  {footerAction.icon}
                  {footerAction.label}
                </Button>
              </div>
            )}
          </Command>
        </div>
      )}
    </div>
  );
}
